"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dataQualitySchema = exports.dataQualityCheckSchema = exports.LOAD_SUMMARY_SOURCES = exports.dataQualityQuerySchema = void 0;
// Wire shapes of Screen 5, Data quality (SPEC §8 Screen 5; §9 `/api/data-quality`; J-038), served at
// `/api/v1/data-quality-checks` (D-973). Check 1 reads the year's loaded bundle, or the year's uploads
// when none is loaded (J-208); check 2 reads the uploads. Checks 3 to 8 and 10
// read the F1 instances and left with them (J-185): the loaded bundle's validation checks and
// Analysis's gate results take their place, by code and result only, or `bundle` is null when no bundle
// is loaded. Numbers are raw (SPEC §9). No row carries a patient's name, date of birth, patient id or
// patient key, so the route is not a PHI route (CR-001 §8 lists it as `acoQuality.read` without PHI).
const zod_1 = require("zod");
const bundle_js_1 = require("../bundle.js");
const groups_js_1 = require("./groups.js");
// No `.meta({ id })` on a querystring schema: @fastify/swagger expands parameters inline.
exports.dataQualityQuerySchema = groups_js_1.performanceGroupsQuerySchema;
const count = zod_1.z.number().int().min(0);
/** One live (not superseded) `core.load_batch` of the year, without its file name or loading user. */
const batchRowSchema = zod_1.z.object({
    batchId: zod_1.z.number().int(),
    sourceType: zod_1.z.string(),
    status: zod_1.z.string(),
    isSynthetic: zod_1.z.boolean(),
    /** ISO 8601, as `/api/v1/metadata` gives it. */
    loadedAt: zod_1.z.string(),
    rowsRead: count,
    rowsLoaded: count,
    rowsRejected: count,
});
/** One `core.load_reject.reason` code with the rejects that carry it. */
const rejectReasonSchema = zod_1.z.object({ reason: zod_1.z.string(), rejects: count });
/**
 * One data file of the year's loaded bundle (J-208): its id, never its name (a file name is shown only
 * to an admin with PHI access, on the Data page), its layout and ACO, and its record counts and
 * set-aside reasons as Analysis reported them.
 */
const bundleFileRowSchema = zod_1.z.object({
    fileId: zod_1.z.string(),
    layout: zod_1.z.string().nullable(),
    /** The VBLTC ACO the file's Analysis code maps to; null for a file of no one ACO. */
    acoCode: zod_1.z.string().nullable(),
    recordsRead: count,
    recordsLoaded: count,
    recordsSetAside: count,
    /** Each set-aside reason with its count, most first, then by code. */
    setAsideReasons: zod_1.z.array(rejectReasonSchema),
});
/**
 * Where check 1 reads from (J-208): `bundle` when the year has a loaded bundle (its data files, their
 * set-aside counts and reasons); `uploads` otherwise (the live load batches, as before CR-004); `none`
 * when the caller's scope holds no ACO.
 */
exports.LOAD_SUMMARY_SOURCES = ["bundle", "uploads", "none"];
/**
 * Check 1: the load summary. `count` is the records set aside across the bundle's files, or the rows
 * rejected across the batches; `rejectReasons` the set-aside or reject reasons with their counts.
 */
const loadSummarySchema = zod_1.z.object({
    check: zod_1.z.literal(1),
    source: zod_1.z.enum(exports.LOAD_SUMMARY_SOURCES),
    count,
    /** The live batches; empty when `source` is `bundle`. */
    rows: zod_1.z.array(batchRowSchema),
    /** The loaded bundle's data files in the caller's ACOs; empty unless `source` is `bundle`. */
    files: zod_1.z.array(bundleFileRowSchema),
    /** Every reason, most first, then by code. */
    rejectReasons: zod_1.z.array(rejectReasonSchema),
});
/**
 * Check 2: `UNMAPPED_MEASURE` rejects by source label (SPEC §3.3). `count` is the rejects, as check 1
 * counts rows: the demo writes one unknown label on 2 rows (`invalid_rows.unknown_measure: 2`).
 */
const unmappedLabelsSchema = zod_1.z.object({
    check: zod_1.z.literal(2),
    count,
    rows: zod_1.z.array(zod_1.z.object({ label: zod_1.z.string(), rejects: count })),
});
/**
 * The year's loaded bundle as Screen 5 shows it (J-185): each validation check and each Analysis gate
 * by code and result. Their counts and differences can name an ACO outside the caller's scope, so they
 * stay on the Data page, which is an admin's.
 */
const loadedBundleSchema = zod_1.z.object({
    bundleId: zod_1.z.uuid(),
    fileName: zod_1.z.string(),
    /** ISO 8601. */
    loadedAt: zod_1.z.string(),
    checks: zod_1.z.array(zod_1.z.object({ code: zod_1.z.string(), result: zod_1.z.enum(["PASS", "FAIL"]) })),
    gates: zod_1.z.array(zod_1.z.object({ gate: zod_1.z.enum(bundle_js_1.BUNDLE_GATES), result: zod_1.z.string() })),
});
exports.dataQualityCheckSchema = zod_1.z.discriminatedUnion("check", [
    loadSummarySchema,
    unmappedLabelsSchema,
]);
exports.dataQualitySchema = zod_1.z
    .object({
    performanceYear: zod_1.z.number().int(),
    /** The ACOs the rows are limited to: the caller's scope, narrowed by `?aco=`. */
    acoCodes: zod_1.z.array(zod_1.z.string()),
    /** Checks 1 and 2. */
    checks: zod_1.z.array(exports.dataQualityCheckSchema).length(2),
    /** The year's loaded bundle's checks and gates, or null when no bundle is loaded. */
    bundle: loadedBundleSchema.nullable(),
    /**
     * J-203: why the Quality report's wording is missing for the scope's measures (the measures
     * document absent or not valid, a measure without a rule table or without wording); empty when
     * every row can be worded. The Quality report and its workbook refuse with the same text.
     */
    reportWordingProblems: zod_1.z.array(zod_1.z.string()),
})
    .meta({ id: "AcoDataQuality" });
//# sourceMappingURL=data-quality.js.map